import { Box, IconButton, TextField } from '@mui/material';
import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { MESSAGE_INPUT_MAX_ROWS } from '../../constants';
import { setComposerEngaged } from '../../state/aiSidekickChatSlice';
import { selectFocusToken } from '../../state/aiSidekickSlice';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import SendArrowIcon from '../icons/SendArrowIcon';
import SidekickDotsIcon, {
  type SidekickIconState,
} from '../icons/SidekickDotsIcon';
import StopSquareIcon from '../icons/StopSquareIcon';
import useStyles from './InputBar.styles';

const DEFAULT_TEST_ID = 'ai-sidekick-input';

/** Props for the chat input bar. */
export type InputBarProps = {
  /**
   * Sends the value. Returning `false` means it was not sent, so the draft
   * stays in the input.
   */
  onSubmit: (value: string) => unknown;
  isStreaming?: boolean;
  /** Whether the M1 visual treatment is active. */
  isM1Enabled?: boolean;
  /** Whether the textarea may receive programmatic focus. */
  isInputFocusEnabled?: boolean;
  /**
   * Optional subscription for raw textarea value changes, including
   * the empty value applied after submission.
   */
  onValueChange?: (value: string) => void;
  /**
   * Stops the running response. When set while `isStreaming`, the send
   * button becomes a stop button.
   */
  onStop?: () => void;
  /**
   * Base `data-testid` for the wrapper, textarea, and send button.
   * The drawer keeps its composer mounted while closed, so a second
   * concurrent input (the dock) must be addressable under its own
   * prefix rather than duplicating the default.
   */
  testId?: string;
};

/** Chat input bar: text input, brand send button, focus-token subscription. */
function InputBar({
  onSubmit,
  isStreaming = false,
  isM1Enabled = false,
  isInputFocusEnabled = true,
  onValueChange,
  onStop,
  testId = DEFAULT_TEST_ID,
}: InputBarProps) {
  const { classes } = useStyles({ isM1Enabled });
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const dispatch = useDispatch();
  const focusToken = useSelector(selectFocusToken);
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const valueRef = useRef<string>(value);
  valueRef.current = value;
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  // Programmatic `.focus()` (mount auto-focus and focus-token re-requests)
  // fires a native focus event that would otherwise read as `listening`.
  // This flag lets the focus handler swallow that one event so the icon
  // only enters `listening` on genuine user focus or typed input.
  const suppressNextFocusRef = useRef(false);

  useEffect(() => {
    // re-fire on focus-request token change; value not read in body
    void focusToken;
    const node = inputRef.current;
    if (!isInputFocusEnabled) {
      // The panel goes `aria-hidden` while its subtree stays in the tab
      // order, so focus must not stay parked on the composer.
      if (node !== null && node === document.activeElement) {
        node.blur();
      }
      return;
    }
    // Only arm the suppression when focus will actually move. Calling
    // `.focus()` on an already-focused element is a no-op that emits no
    // focus event, so arming the flag there would leave it set and swallow
    // the next genuine user focus — stranding the icon in `standby`.
    if (node !== null && node !== document.activeElement) {
      suppressNextFocusRef.current = true;
      node.focus();
    }
  }, [focusToken, isInputFocusEnabled]);

  const handleFocus = useCallback(() => {
    if (suppressNextFocusRef.current) {
      suppressNextFocusRef.current = false;
      return;
    }
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const submit = useCallback(() => {
    const current = valueRef.current.trim();
    if (isStreaming || current.length === 0) {
      return;
    }
    if (onSubmit(current) === false) {
      return;
    }
    setValue('');
    onValueChange?.('');
  }, [isStreaming, onSubmit, onValueChange]);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const next = event.target.value;
    setValue(next);
    onValueChange?.(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Guard against IME composition (CJK, accent/diacritic flows): Enter
    // commits the in-progress composition there, so submitting would send
    // the message mid-composition.
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      submit();
    }
  };

  // The composer is "engaged" once the user types content or genuinely
  // focuses the input (the mount auto-focus is suppressed). Mirrors the
  // icon's `listening` precedent below.
  const isComposerEngaged = value.length > 0 || isFocused;

  // Publish the engaged signal to the chat slice so the entry-point
  // card's brand mark mirrors this composer's `listening` state. The
  // unmount cleanup resets the flag so a closed panel can't strand the
  // entry icon in `listening`.
  useEffect(() => {
    dispatch(setComposerEngaged(isComposerEngaged));
  }, [dispatch, isComposerEngaged]);
  useEffect(
    () => () => {
      dispatch(setComposerEngaged(false));
    },
    [dispatch]
  );

  // Brand-icon animation state for the Phase B drawer composer:
  // `thinking` while a response streams (highest precedence), else
  // `listening` once the user engages the input, else `standby`.
  const iconState: SidekickIconState = isStreaming
    ? 'thinking'
    : isComposerEngaged
      ? 'listening'
      : 'standby';

  const textFieldSlotProps = {
    input: { disableUnderline: true },
    htmlInput: { 'data-testid': `${testId}-text` },
  } as const;

  return (
    <Box className={classes.wrapper} data-testid={`${testId}-bar`}>
      {isM1Enabled ? null : (
        <Box className={classes.brandIconSlot} aria-hidden="true">
          <SidekickDotsIcon state={iconState} decorative size={16} />
        </Box>
      )}
      <TextField
        className={classes.textareaRoot}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={t('chat.input.placeholder')}
        variant="standard"
        fullWidth
        multiline
        minRows={1}
        maxRows={MESSAGE_INPUT_MAX_ROWS}
        inputRef={inputRef}
        slotProps={textFieldSlotProps}
      />
      {isStreaming && onStop !== undefined ? (
        <IconButton
          className={classes.sendButton}
          onClick={onStop}
          aria-label={t('chat.input.stopAriaLabel')}
          data-testid={`${testId}-stop`}
        >
          <StopSquareIcon size={16} />
        </IconButton>
      ) : (
        <IconButton
          className={classes.sendButton}
          onClick={submit}
          aria-label={t('chat.input.sendAriaLabel')}
          data-testid={`${testId}-send`}
        >
          <SendArrowIcon size={16} />
        </IconButton>
      )}
    </Box>
  );
}

export default InputBar;
